import React, { useEffect, useState } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useGetAccount, useGetNetworkConfig, useGetPendingTransactions, useGetIsLoggedIn } from '@multiversx/sdk-dapp/hooks';
import { Backdrop, Box, Modal } from '@mui/material';
import { animated, useSpring } from 'react-spring';
import BigNumber from 'bignumber.js';
import egld_white_logo from 'assets/img/EGLD_white.svg';
import { queryViewJewelFarmBaseContext, queryViewPositionsByIds, queryViewPositionsSafetyContextByIds } from 'z/elrond';
import { CommonSettingsType, EsdtTokenPayment, JewelFarmBaseContext, JewelFarmContext, JewelFarmPosition, JewelFarmPositionSafetyContext, JewelFarmPositionStateEnum, LoanType } from 'z/types';
import { convertBigNumberValueToLocalString, convertWeiToEsdt, createNftId, ERROR_CONNECT_YOUR_WALLET, ERROR_DATA_NOT_LOADED, ERROR_NOT_ENOUGH_BALANCE, EXTRA_GAS_FEE_AMOUNT, formatLocalDateTime, getTokenDecimal, getTokenImage, getTokenTicker, MAX_PERCENTAGE, SECOND_TO_MILLISECONDS, sleep, toastError } from 'z/utils';
import { isMobile } from 'react-device-detect';
import { JEWEL_ASHSWAP_SC_ADDRESS, SLIPPAGE } from 'config';
import { exitFarm } from 'z/elrond/jewel-farm';

const fadeBoxStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: 300,
  // bgcolor: '#2b3943',
  boxShadow: 24,
  px: 2,
  py: 2,
  borderRadius: '10px',
  color: 'white',
  background: 'linear-gradient(180deg, #2b3943, #1a242b)',
};

interface FadeProps {
  children?: React.ReactElement;
  in: boolean;
  onEnter?: () => void;
  onExited?: () => void;
}

const Fade = React.forwardRef<HTMLDivElement, FadeProps>(function Fade(props, ref) {
  const { in: open, children, onEnter, onExited, ...other } = props;
  const style = useSpring({
    from: { opacity: 0 },
    to: { opacity: open ? 1 : 0 },
    onStart: () => {
      if (open && onEnter) {
        onEnter();
      }
    },
    onRest: () => {
      if (!open && onExited) {
        onExited();
      }
    },
  });

  return (
    <animated.div ref={ref} style={style} {...other}>
      {children}
    </animated.div>
  );
});

export const MyPositions = () => {
  const {
    network: { apiAddress },
    chainID,
  } = useGetNetworkConfig();
  const { address, balance } = useGetAccount();
  const { hasPendingTransactions } = useGetPendingTransactions();
  const isLoggedIn = useGetIsLoggedIn();

  const [showClosePositionModal, setShowClosePositionModal] = useState<boolean>(false);
  const [commonSettings, setCommonSettings] = useState<JewelFarmBaseContext | undefined>();
  const [positionsLoading, setPositionsLoading] = useState<boolean>(false);
  const [opendPositions, setOpendPositions] = useState<JewelFarmPosition[]>([]);
  const [positionSafety, setPositionSafety] = useState<JewelFarmPositionSafetyContext[]>([]);
  const [selectedPosition, setSelectedPosition] = useState<JewelFarmPosition | undefined>();
  useEffect(() => {
    if (hasPendingTransactions || !isLoggedIn) return;

    (async () => {
      setPositionsLoading(true);
      const _commonSettings = await queryViewJewelFarmBaseContext(apiAddress, address, balance);
      console.log('_commonSettings', _commonSettings);
      setCommonSettings(_commonSettings);

      if (_commonSettings) {
        const _positions = await queryViewPositionsByIds(apiAddress, _commonSettings.last_position_id);
        // console.log('_positions: ', _positions);
        const opendItems: JewelFarmPosition[] = [];
        const positionIds: number[] = [];
        for (let i = 0; i < _positions.length; i++) {
          if (_positions[i].position_owner === address && _positions[i].position_state === JewelFarmPositionStateEnum.Opened) {
            opendItems.push(_positions[i]);
            positionIds.push(_positions[i].position_id);
          }
        }

        //
        if (positionIds.length > 0) {
          const positionSafety = await queryViewPositionsSafetyContextByIds(apiAddress, positionIds);
          console.log('positionSafety: ', positionSafety);
          setPositionSafety(positionSafety);

          console.log('opendItems: ', opendItems);
          setOpendPositions(opendItems);
        }
      }
      setPositionsLoading(false);

    })();
  }, [hasPendingTransactions]);

  const handleOpenClosePositionModal = (item: JewelFarmPosition) => {
    setSelectedPosition(item);
    setShowClosePositionModal(true);
  };

  const handleClosePosition = async () => {

    if (selectedPosition) {
      await exitFarm(chainID, JEWEL_ASHSWAP_SC_ADDRESS, address, selectedPosition?.position_id, SLIPPAGE * MAX_PERCENTAGE);

      setShowClosePositionModal(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'position_id',
      headerName: '#',
      width: isMobile ? 120 : 0,
      flex: isMobile ? 0 : 2,
      renderCell: (params) => (
        <div className='py-2'>
          {`Jewel #${params.value}`}
        </div>
      ),
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'position_farm_id',
      headerName: 'Pool',
      width: isMobile ? 120 : 0,
      flex: isMobile ? 0 : 2,
      renderCell: (params) => (
        <div className='py-2'>
          {commonSettings && commonSettings.farms.length >= params.value ? (
            <div>
              {commonSettings.farms[params.value - 1].token_ids.map((item: string, key: number) => {
                let poolName = '';
                if (key === (commonSettings.farms[params.value - 1].token_ids.length - 1)) {
                  poolName += getTokenTicker(item);
                } else {
                  poolName += `${getTokenTicker(item)}-`;
                }
                return poolName;
              })}
            </div>
          ) : ''}
        </div>
      ),
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'position_farm_apy',
      headerName: 'APY',
      width: isMobile ? 100 : 0,
      flex: isMobile ? 0 : 2,
      renderCell: (params) => (
        <div className='py-2'>
          {commonSettings && commonSettings.farms.length >= params.row.position_farm_id ? (
            <div>
              {`${commonSettings.farms[params.row.position_farm_id - 1].apy.toFixed(2)} %`}
            </div>
          ) : ''}
        </div>
      ),
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'position_state',
      headerName: 'State',
      width: isMobile ? 120 : 0,
      flex: isMobile ? 0 : 2,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'position_value',
      headerName: 'Position Value',
      width: isMobile ? 150 : 0,
      flex: isMobile ? 0 : 3,
      renderCell: (params) => {
        const safetyValue = positionSafety.filter((item: JewelFarmPositionSafetyContext) => item.position_id === params.row.position_id);
        return <div>
          {safetyValue.length > 0 && safetyValue[0].value ? (
            <div className='d-flex gap-2 align-items-center'>
              <img className='egld-badge' src={getTokenImage(params.row.position_debt_token)} alt='logo' />
              <span>{convertBigNumberValueToLocalString(convertWeiToEsdt(safetyValue[0].value, getTokenDecimal(params.row.position_debt_token)))}</span>
            </div>
          ) : (<></>)}
        </div>;
      },
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'position_debt_amount',
      headerName: 'Debt Value',
      width: isMobile ? 150 : 0,
      flex: isMobile ? 0 : 3,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <div className='d-flex gap-2 align-items-center'>
          <img className='egld-badge' src={getTokenImage(params.row.position_debt_token)} alt='logo' />
          <span>{convertBigNumberValueToLocalString(convertWeiToEsdt(params.value, getTokenDecimal(params.row.position_debt_token)))}</span>
        </div>
      ),
    },
    {
      field: 'equity_value',
      headerName: 'Equity Value',
      width: isMobile ? 150 : 0,
      flex: isMobile ? 0 : 3,
      renderCell: (params) => {
        const safetyValue = positionSafety.filter((item: JewelFarmPositionSafetyContext) => item.position_id === params.row.position_id);
        return <div>
          {safetyValue.length > 0 && safetyValue[0].value ? (
            <div className='d-flex gap-2 align-items-center'>
              <img className='egld-badge' src={getTokenImage(params.row.position_debt_token)} alt='logo' />
              <span>{convertBigNumberValueToLocalString(convertWeiToEsdt(BigNumber(safetyValue[0].value).minus(params.row.position_debt_amount), getTokenDecimal(params.row.position_debt_token)))}</span>
            </div>
          ) : (<></>)}
        </div>;
      },
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'position_debt_ratio',
      headerName: 'Debt Ratio',
      width: isMobile ? 120 : 0,
      flex: isMobile ? 0 : 2,
      renderCell: (params) => {
        const safetyValue = positionSafety.filter((item: JewelFarmPositionSafetyContext) => item.position_id === params.row.position_id);
        return <div>
          {safetyValue.length > 0 ? (
            <div className='d-flex gap-2 align-items-center'>
              <span>{(safetyValue[0].debt_ratio / MAX_PERCENTAGE).toFixed(2)}%</span>
            </div>
          ) : (<></>)}
        </div>;
      },
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'position_liquidation_threshold',
      headerName: 'Liquidation Threshold',
      width: isMobile ? 120 : 0,
      flex: isMobile ? 0 : 2,
      renderCell: (params) => {
        const safetyValue = positionSafety.filter((item: JewelFarmPositionSafetyContext) => item.position_id === params.row.position_id);
        return <div>
          {safetyValue.length > 0 ? (
            <div className='d-flex gap-2 align-items-center'>
              <span>{(safetyValue[0].liquidation_threshold / MAX_PERCENTAGE).toFixed(2)}%</span>
            </div>
          ) : (<></>)}
        </div>;
      },
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'position_safety_buffer',
      headerName: 'Safety Buffer',
      width: isMobile ? 120 : 0,
      flex: isMobile ? 0 : 2,
      renderCell: (params) => {
        const safetyValue = positionSafety.filter((item: JewelFarmPositionSafetyContext) => item.position_id === params.row.position_id);
        let safetyBuffer = 0;
        if (safetyValue[0].liquidation_threshold >= safetyValue[0].debt_ratio) {
          safetyBuffer = safetyValue[0].liquidation_threshold - safetyValue[0].debt_ratio;
        } else {
          safetyBuffer = safetyValue[0].debt_ratio - safetyValue[0].liquidation_threshold;
        }
        return <div>
          {safetyValue.length > 0 ? (
            <div className='d-flex gap-2 align-items-center'>
              <span>{(safetyBuffer / MAX_PERCENTAGE).toFixed(2)}%</span>
            </div>
          ) : (<></>)}
        </div>;
      },
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'position_leverage_percent',
      headerName: 'Leverage Percentage',
      width: isMobile ? 100 : 0,
      flex: isMobile ? 0 : 2,
      renderCell: (params) => (
        <div className='d-flex gap-2 align-items-center'>
          <span>{(params.value / MAX_PERCENTAGE / 100)}X</span>
        </div>
      ),
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'button',
      headerName: 'Action',
      width: isMobile ? 100 : 0,
      flex: isMobile ? 0 : 3,
      renderCell: (params) => (
        <button className='approval_but' onClick={() => handleOpenClosePositionModal(params.row)}>Close</button>
      ),
      align: 'center',
      headerAlign: 'center',
      sortable: false,
    },
  ];

  return (
    <>
      <div className='container ' style={{ marginTop: '40px' }}>
        <div className='info-item'>
          <div className='d-flex justify-content-between align-items-center'>
            <div className='title'>
              <span>My Positions</span>
            </div>
            <div className='egld-logo' >
              <img src={egld_white_logo} />
            </div>
          </div>

          <div className='w-100' style={{ marginTop: '30px' }}>
            <DataGrid
              sx={{
                '& .MuiSvgIcon-root': { fontSize: 20 },
                fontSize: '12px',
                '& .MuiDataGrid-columnSeparator': {
                  visibility: 'hidden',
                },
                '& .MuiDataGrid-virtualScroller': {
                  minHeight: '400px'
                },
              }}
              autoHeight
              getRowHeight={() => 'auto'}
              rows={opendPositions}
              columns={columns}
              getRowId={(row) => row.position_id}
              disableColumnMenu
              disableSelectionOnClick
              hideFooter
              loading={positionsLoading}
            />
          </div>
        </div>
      </div>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={showClosePositionModal}
        onClose={() => setShowClosePositionModal(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={showClosePositionModal}>
          <Box sx={fadeBoxStyle} className='farm-modal scrollbar' style={{ width: 'auto', maxHeight: '95%', overflowY: 'scroll' }}>
            <div className='d-flex mt-4 justify-content-between'>
              <div className='d-flex align-items-center'>
                Initial Deposit Tokens
              </div>
              <div className='d-flex align-items-center'>
                {selectedPosition && (
                  <>
                    {
                      selectedPosition.position_deposit_payments.map((item: EsdtTokenPayment, index: number) => {
                        return `${convertBigNumberValueToLocalString(convertWeiToEsdt(item.amount, getTokenDecimal(item.token_identifier)))} ${getTokenTicker(item.token_identifier)} ${selectedPosition.position_deposit_payments.length === (index + 1) ? '' : ' + '}`;
                      })
                    }
                  </>
                )}
              </div>
            </div>
            <div className='d-flex mt-2 justify-content-between'>
              <div className='d-flex align-items-center'>
                Initial Equity Value
              </div>
              <div className='d-flex align-items-center'>
                {selectedPosition && (
                  <>
                    {`${convertBigNumberValueToLocalString(convertWeiToEsdt(BigNumber(selectedPosition.position_debt_amount).multipliedBy(selectedPosition.position_leverage_percent / 100 / MAX_PERCENTAGE - 1), getTokenDecimal(selectedPosition.position_debt_token)))} ${getTokenTicker(selectedPosition.position_debt_token)}`}
                  </>
                )}
              </div>
            </div>
            <div className='d-flex mt-2 justify-content-between'>
              <div className='d-flex align-items-center'>
                Current Equity Value
              </div>
              <div className='d-flex align-items-center'>
                {selectedPosition && (
                  <>
                    {`${convertBigNumberValueToLocalString(convertWeiToEsdt(BigNumber(positionSafety.filter((item: JewelFarmPositionSafetyContext) => item.position_id === selectedPosition.position_id)[0].value).minus(selectedPosition.position_debt_amount), getTokenDecimal(selectedPosition.position_debt_token)))} ${getTokenTicker(selectedPosition.position_debt_token)}`}
                  </>
                )}
              </div>
            </div>
            {selectedPosition && commonSettings && (
              <div className='d-flex mt-4'>
                {commonSettings.farms.filter(farm => farm.farm_id === selectedPosition.position_farm_id)[0].token_ids.map((item: string, index: number) => (
                  <div className='d-flex mx-1' key={index}>
                    <img src={getTokenImage(item)} className='egld-badge'></img>
                    <div className='d-flex align-items-center mx-1'>
                      {`1 ${getTokenTicker(item)} = ${commonSettings.ashswap_tokens.data.tokens.length > 0 ? commonSettings.ashswap_tokens.data.tokens.filter((token: any) => token.id === item)[0]['price'] : '-'} USD`}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className='d-flex justify-content-center mt-4'>
              <div className="eg-btn btn--primary2 capsule px-4 py-2" style={{ cursor: 'pointer' }} onClick={handleClosePosition}>
                Close Position
              </div>
            </div>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};
